import _ from "lodash";
import localForage from "localforage";
import { useEffect, useState } from "react";
import {
  FLOW,
  CARD,
  TABLE,
  TIMELINE,
  ADD_ITEM_TO_TOP_PREFERENCE,
  getAddNewItemToTopPreference,
  getDisplaySettingsConfig,
  DISPLAY_SETTINGS_CONFIG_NAME,
  SCHEDULE,
} from "./EntityHelpers";
import {
  CURRICULUM_LEVEL,
  UPDATE_USER_CURRICULUM_SETTINGS,
} from "Constants/permissionConstants";

const DEFAULT_DATA = "[]";

const entityFlowStore = localForage.createInstance({
  name: "entityFlow",
});

const DEFAULT_FLOW_DISPLAY_SETTINGS = {
  show_outline: true,
  collapse_all_sections: false,
  expand_all_sections: true,
  collapse_expand_action_nonce: 0,
  thumbnail_image: true,
  task_resource_type: false,
  task_status: true,
  add_to_top: false,
  add_to_bottom: true,
  assessment_category: true,
  due_or_scheduled_date: true,
  locked_status: true,
  show_blueprint_lock: true,
  grading_period: true,
};

const DEFAULT_UNIT_DISPLAY_SETTINGS = {
  sort: {
    TITLE: false,
    UPDATED_AT: false,
    START_DATE: true,
    CUSTOM: false,
  },
  display: {
    subjects: true,
    unit_hours: true,
    duration_month: true,
    level: true,
    duration_week: true,
    learning_experiences: true,
    assessments: true,
    collaborators: true,
    shared_with_students_family: true,
    show_blueprint_lock: true,
    owner: true,
    unit_code: true,
  },
  group_by_unit_type: true,
};

const DEFAULT_SCHEDULE_DISPLAY_SETTINGS = {
  view_timestamp_on_left_axis: false,
  show_calendar_events: true,
  show_scheduled_lesson_content: true,
  view_subjects: false,
  view_locations: true,
  qt: true,
  le: true,
  assessment: true,
  worksheet: true,
  ai_tutor: true,
  NOTE: true,
  LINK: true,
  REST_RESOURCES: true,
};

export const useCollapsePreference = ({ entityType, entityId }) => {
  const storeId = `${entityType}-${entityId}`;

  const [isLoading, setIsLoading] = useState(true);
  const [collapsedItems, setCollapsedItems] = useState(new Set());

  // load items from storage on initial mount
  useEffect(() => {
    (async () => {
      try {
        let storedData = await entityFlowStore.getItem(storeId);

        // initialize storage if reading for the first time
        if (_.isEmpty(storedData)) {
          await entityFlowStore.setItem(storeId, DEFAULT_DATA);
          storedData = DEFAULT_DATA;
        }
        storedData = JSON.parse(storedData);
        const storedDataSet = new Set(storedData);

        setCollapsedItems(storedDataSet);
      } catch (e) {
        console.error(
          "Encountered error while reading from entity flow store!",
          e
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [storeId]);

  // sync storage with our local state
  useEffect(() => {
    (async () => {
      try {
        const expandedItemsAsArray = Array.from(collapsedItems);
        await entityFlowStore.setItem(
          storeId,
          JSON.stringify(expandedItemsAsArray)
        );
      } catch (e) {
        console.error(
          "Encountered error while writing to entity flow store!",
          e
        );
      }
    })();
  }, [collapsedItems]);

  return {
    collapsedItems,
    setCollapsedItems,
    isLoading,
  };
};

const ALL_DEFAULT_DISPLAY_SETTINGS = {
  ...DEFAULT_FLOW_DISPLAY_SETTINGS,
  ...DEFAULT_UNIT_DISPLAY_SETTINGS,
  ...DEFAULT_SCHEDULE_DISPLAY_SETTINGS,
};

export const useDisplaySettingsPreference = ({
  entityType,
  entityId,
  updateSettings,
}) => {
  const storeId = `${entityType}_${entityId}_DISPLAY_SETTINGS`;

  const [isLoading, setIsLoading] = useState(true);
  const [displaySettings, setDisplaySettings] = useState(
    ALL_DEFAULT_DISPLAY_SETTINGS
  );
  const [
    defaultAddNewItemsToTopPreference,
    setDefaultAddNewItemsToTopPreference,
  ] = useState(null);

  // load settings from storage on initial mount
  useEffect(() => {
    const preference = getAddNewItemToTopPreference();
    const preferenceValue = _.isBoolean(preference) ? preference : false;
    setDefaultAddNewItemsToTopPreference(preferenceValue);

    (async () => {
      try {
        let storedData = await entityFlowStore.getItem(storeId);

        // initialize storage if reading for the first time
        if (_.isEmpty(storedData)) {
          storedData = ALL_DEFAULT_DISPLAY_SETTINGS;
          await entityFlowStore.setItem(storeId, JSON.stringify(storedData));
        }

        const parsedData =
          typeof storedData === "string" ? JSON.parse(storedData) : storedData;

        const storedDisplaySettings = {
          ...ALL_DEFAULT_DISPLAY_SETTINGS,
          ...parsedData,
          add_to_top: preferenceValue,
          add_to_bottom: !preferenceValue,
        };

        setDisplaySettings(storedDisplaySettings);
      } catch (e) {
        console.error(
          "Encountered error while reading display settings from entity flow store!",
          e
        );
      } finally {
        setIsLoading(false);
      }
    })();
  }, [storeId]);

  // sync storage with our local state
  useEffect(() => {
    if (isLoading) return; // Don't sync during initial load

    try {
      entityFlowStore.setItem(storeId, JSON.stringify(displaySettings));
      const currentAddNewItemsToTopPreference = _.get(
        displaySettings,
        "add_to_top",
        defaultAddNewItemsToTopPreference
      );

      if (
        currentAddNewItemsToTopPreference !== defaultAddNewItemsToTopPreference
      ) {
        setDefaultAddNewItemsToTopPreference(currentAddNewItemsToTopPreference);

        if (updateSettings) {
          updateSettings?.({
            settings: [
              {
                name: ADD_ITEM_TO_TOP_PREFERENCE,
                value: String(currentAddNewItemsToTopPreference),
              },
            ],
            showToastMsg: false,
          });
        }
      }
    } catch (e) {
      console.error(
        "Encountered error while writing display settings to entity flow store!",
        e
      );
    }
  }, [displaySettings, storeId, isLoading, defaultAddNewItemsToTopPreference]);

  return {
    displaySettings,
    setDisplaySettings,
    isLoading,
  };
};

const LOCAL_FLOW_SETTINGS = [
  "show_outline",
  "collapse_all_sections",
  "expand_all_sections",
];

const LOCAL_AND_ADD_TO_TOP_FLOW_SETTINGS = [
  ...LOCAL_FLOW_SETTINGS,
  "add_to_top",
  "add_to_bottom",
];

/**
 * V2 hook for CoursePlan display settings that handles multiple storage mechanisms:
 *
 * Flow settings:
 * - show_outline, collapse_all_sections, expand_all_sections: localStorage (module-specific)
 * - add_to_top, add_to_bottom: ADD_ITEM_TO_TOP_PREFERENCE config
 * - other settings: DISPLAY_SETTINGS_CONFIG_NAME config (flow key)
 *
 * Unit view settings (card, timeline, table):
 * - all settings: DISPLAY_SETTINGS_CONFIG_NAME config (card/timeline/table keys)
 * - each view has independent settings stored separately
 *
 * Schedule view settings:
 * - all settings: DISPLAY_SETTINGS_CONFIG_NAME config (schedule key)
 *
 * Returns:
 * - displaySettings: { flow: {...}, card: {...}, timeline: {...}, table: {...}, schedule: {...} }
 * - setDisplaySettings: function to update settings
 * - isLoading: boolean
 */
export const useDisplaySettingsPreferenceV2 = ({
  entityType,
  entityId,
  updateSettings,
  curriculumProgramId,
  customKey,
}) => {
  const localStoreId = `${entityType}_${entityId}_DISPLAY_SETTINGS`;

  const [isLoading, setIsLoading] = useState(true);
  const [displaySettings, setDisplaySettingsState] = useState({
    [FLOW]: DEFAULT_FLOW_DISPLAY_SETTINGS,
    [CARD]: DEFAULT_UNIT_DISPLAY_SETTINGS,
    [TIMELINE]: DEFAULT_UNIT_DISPLAY_SETTINGS,
    [TABLE]: DEFAULT_UNIT_DISPLAY_SETTINGS,
    [SCHEDULE]: DEFAULT_SCHEDULE_DISPLAY_SETTINGS,
  });
  const [addToTopPreference, setAddToTopPreference] = useState(null);

  // Settings that use localStorage

  // Load settings on mount
  useEffect(() => {
    const preference = getAddNewItemToTopPreference();
    const preferenceValue = _.isBoolean(preference) ? preference : false;
    setAddToTopPreference(preferenceValue);

    // Read Apollo config synchronously before the async localForage read.
    // useEffect runs after commit, so Apollo cache is populated at this point.
    // Reading inside the async IIFE (after an await) risks stale values on hard refresh.
    const configValue = getDisplaySettingsConfig();

    (async () => {
      try {
        //Get localStorage-based flow settings
        const localStoredData = await entityFlowStore.getItem(localStoreId);
        const parsedLocalData =
          typeof localStoredData === "string" && !_.isEmpty(localStoredData)
            ? JSON.parse(localStoredData)
            : {};
        const configFlowSettings = _.get(configValue, FLOW, {});
        const configCardSettings = _.get(configValue, CARD, {});
        const configTimelineSettings = _.get(configValue, TIMELINE, {});
        const configTableSettings = _.get(configValue, TABLE, {});
        const configScheduleSettings = _.get(configValue, SCHEDULE, {});

        // Merge flow settings: defaults -> config -> localStorage -> add_to preferences
        const mergedFlowSettings = {
          ...DEFAULT_FLOW_DISPLAY_SETTINGS,
          ...configFlowSettings,
          ..._.pick(parsedLocalData, LOCAL_FLOW_SETTINGS),
          add_to_top: preferenceValue,
          add_to_bottom: !preferenceValue,
        };

        // Merge unit view settings: defaults -> config
        const mergedCardSettings = _.merge(
          {},
          DEFAULT_UNIT_DISPLAY_SETTINGS,
          configCardSettings
        );

        const mergedTimelineSettings = _.merge(
          {},
          DEFAULT_UNIT_DISPLAY_SETTINGS,
          configTimelineSettings
        );

        const mergedTableSettings = _.merge(
          {},
          DEFAULT_UNIT_DISPLAY_SETTINGS,
          configTableSettings
        );

        const mergedScheduleSettings = _.merge(
          {},
          DEFAULT_SCHEDULE_DISPLAY_SETTINGS,
          configScheduleSettings
        );

        setDisplaySettingsState({
          [FLOW]: mergedFlowSettings,
          [CARD]: mergedCardSettings,
          [TIMELINE]: mergedTimelineSettings,
          [TABLE]: mergedTableSettings,
          [SCHEDULE]: mergedScheduleSettings,
        });
      } catch (e) {
        console.error("Encountered error while reading display settings!", e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [localStoreId]);

  // Helper to save config settings
  const saveConfigSettings = (
    flowSettings,
    cardSettings,
    timelineSettings,
    tableSettings,
    scheduleSettings
  ) => {
    const configFlowSettings = _.omit(
      flowSettings,
      LOCAL_AND_ADD_TO_TOP_FLOW_SETTINGS
    );

    const updatedConfig = {
      [FLOW]: configFlowSettings,
      [CARD]: cardSettings,
      [TIMELINE]: timelineSettings,
      [TABLE]: tableSettings,
      [SCHEDULE]: scheduleSettings,
    };

    if (updateSettings) {
      updateSettings({
        settings: [
          {
            name: DISPLAY_SETTINGS_CONFIG_NAME,
            value: JSON.stringify(updatedConfig),
          },
        ],
        currentCurriculumProgramId: curriculumProgramId,
        queryVariablesSrc: CURRICULUM_LEVEL,
        mutationVariablesSrc: UPDATE_USER_CURRICULUM_SETTINGS,
        showToastMsg: false,
      });
    }
  };

  // Helper to handle add_to preference changes
  const handleAddToTopPreferenceChange = newFlowSettings => {
    const newAddToTop = _.get(newFlowSettings, "add_to_top", false);
    if (newAddToTop !== addToTopPreference) {
      setAddToTopPreference(newAddToTop);
      if (updateSettings) {
        updateSettings({
          settings: [
            {
              name: ADD_ITEM_TO_TOP_PREFERENCE,
              value: String(newAddToTop),
            },
          ],
          currentCurriculumProgramId: curriculumProgramId,
          queryVariablesSrc: CURRICULUM_LEVEL,
          mutationVariablesSrc: UPDATE_USER_CURRICULUM_SETTINGS,
          showToastMsg: false,
        });
      }
    }
  };

  // Set display settings with proper storage handling
  const setDisplaySettings = newDisplaySettings => {
    const newFlowSettings = _.get(newDisplaySettings, FLOW);
    const newCardSettings = _.get(newDisplaySettings, CARD);
    const newTimelineSettings = _.get(newDisplaySettings, TIMELINE);
    const newTableSettings = _.get(newDisplaySettings, TABLE);
    const newScheduleSettings = _.get(newDisplaySettings, SCHEDULE);

    const currentFlowSettings = _.get(displaySettings, FLOW);
    const currentCardSettings = _.get(displaySettings, CARD);
    const currentTimelineSettings = _.get(displaySettings, TIMELINE);
    const currentTableSettings = _.get(displaySettings, TABLE);
    const currentScheduleSettings = _.get(displaySettings, SCHEDULE);

    // Update state optimistically
    setDisplaySettingsState(newDisplaySettings);

    // Save localStorage settings for flow if changed
    if (newFlowSettings) {
      const newLocalStorageSettings = _.pick(
        newFlowSettings,
        LOCAL_FLOW_SETTINGS
      );
      const currentLocalStorageSettings = _.pick(
        currentFlowSettings,
        LOCAL_FLOW_SETTINGS
      );

      if (!_.isEqual(newLocalStorageSettings, currentLocalStorageSettings)) {
        entityFlowStore.setItem(
          localStoreId,
          JSON.stringify({ ...newLocalStorageSettings })
        );
      }

      handleAddToTopPreferenceChange(newFlowSettings);
    }

    // Save config settings only if changed
    const newConfigFlowSettings = _.omit(
      newFlowSettings,
      LOCAL_AND_ADD_TO_TOP_FLOW_SETTINGS
    );
    const currentConfigFlowSettings = _.omit(
      currentFlowSettings,
      LOCAL_AND_ADD_TO_TOP_FLOW_SETTINGS
    );

    // FLOW
    const configFlowChanged = !_.isEqual(
      newConfigFlowSettings,
      currentConfigFlowSettings
    );

    // CARD
    const configCardChanged = !_.isEqual(newCardSettings, currentCardSettings);

    // TIMELINE
    const configTimelineChanged = !_.isEqual(
      newTimelineSettings,
      currentTimelineSettings
    );

    // TABLE
    const configTableChanged = !_.isEqual(
      newTableSettings,
      currentTableSettings
    );

    // SCHEDULE
    const configScheduleChanged = !_.isEqual(
      newScheduleSettings,
      currentScheduleSettings
    );

    if (
      configFlowChanged ||
      configCardChanged ||
      configTimelineChanged ||
      configTableChanged ||
      configScheduleChanged
    ) {
      saveConfigSettings(
        newFlowSettings,
        newCardSettings,
        newTimelineSettings,
        newTableSettings,
        newScheduleSettings
      );
    }
  };

  if (customKey) {
    return {
      displaySettings: displaySettings[customKey],
      setDisplaySettings: newFlowSettings =>
        setDisplaySettings({
          ...displaySettings,
          [customKey]: newFlowSettings,
        }),
      isLoading,
    };
  } else {
    return {
      displaySettings,
      setDisplaySettings,
      isLoading,
    };
  }
};

export const clearEntityFlowStore = async () => {
  await entityFlowStore.clear();
};
